/**
 * The moments (docs/mockups/notifications.html, slide 2): a small card at a time a
 * notification is obviously worth having, whose yes opens the sheet with that moment's
 * switch first. Never the browser's prompt (only the sheet's button asks).
 *
 * - On the result: the first solve ("Tomorrow's is out at midnight"), or, on a streak of
 *   two or more, "Reminders for your {n}-day streak". Never on a challenge's result.
 * - On Nest, after joining or starting a nest: "Know when {Nest} plays".
 * - After sending a nudge: a toast, "{name} can nudge you back", with Turn on.
 *
 * At most one a day, never before the first solve, and "Not now" rests them all (7 days,
 * then 30, then for good): the rules are state.ts `momentVisible`.
 */
import { localDateKey } from '@arrow/game-engine';
import { type ReactNode, useCallback, useEffect, useRef, useState } from 'react';

import { tokenMs } from '../app/motion.ts';
import { Nib } from '../components/Nib.tsx';
import { type NotifySurface, track } from '../platform/analytics.ts';
import { Toast } from '../ui/index.ts';
import { openNotifySheet } from './host.tsx';
import { clearNestJoin, noteMomentShown, noteNotNow, useNotifyStatus } from './push.ts';
import { MOMENT_GROUP, type MomentKind, momentVisible, notNowLine, pendingNest } from './state.ts';
import '../styles/push.css';

/** Whether `kind`'s moment shows now, and its yes and Not now. Counted as shown once per mount. */
export function useMoment(kind: MomentKind, { solvedBefore, active = true }: { solvedBefore: boolean; active?: boolean }) {
	const { state, push } = useNotifyStatus();
	const today = localDateKey(new Date());
	const [said, setSaid] = useState<string | null>(null);
	// Once they're on (from its sheet, or the bell), it goes.
	const visible = active && !!state && momentVisible(push.moments, kind, { today, state, solvedBefore });
	const counted = useRef(false);
	useEffect(() => {
		if (!visible || counted.current) return;
		counted.current = true;
		noteMomentShown(kind, today);
		track('notify_ask_shown', { surface: kind });
	}, [visible, kind, today]);
	const yes = useCallback(() => {
		track('notify_ask_tapped', { surface: kind });
		if (kind === 'nest_join') clearNestJoin();
		openNotifySheet({ surface: kind as NotifySurface, first: MOMENT_GROUP[kind] });
	}, [kind]);
	const notNow = useCallback(() => {
		track('notify_ask_dismissed', { surface: kind });
		setSaid(notNowLine(noteNotNow(today)));
	}, [kind, today]);
	return { visible, said, yes, notNow, nest: pendingNest(push.moments, today), saverAt: push.prefs.streakSaverAt };
}

function Card({
	art,
	title,
	body,
	yes,
	onYes,
	onNotNow,
	testId,
}: {
	/** Left of the words; none on the streak's card, whose flame is on the poster just above. */
	art?: ReactNode;
	title: string;
	body: string;
	yes: string;
	onYes: () => void;
	onNotNow: () => void;
	testId: string;
}) {
	return (
		<section className={art ? 'nmo' : 'nmo bare'} aria-labelledby={`${testId}-t`} data-testid={testId}>
			{art && <span className="nmo-art">{art}</span>}
			<div className="nmo-b">
				<h3 className="nmo-t" id={`${testId}-t`}>
					{title}
				</h3>
				<p className="nmo-p">{body}</p>
				<div className="nmo-acts">
					<button type="button" className="btn sm primary" onClick={onYes}>
						{yes}
					</button>
					<button type="button" className="tlink" onClick={onNotNow}>
						Not now
					</button>
				</div>
			</div>
		</section>
	);
}

function Said({ text, testId }: { text: string; testId: string }) {
	return (
		<p className="nmo-said" role="status" data-testid={testId}>
			{text}
		</p>
	);
}

/** The result poster's moment, below its crop line: `kind` from state.ts `resultMoment`. */
export function ResultMoment({ kind, streak }: { kind: 'first_solve' | 'streak_solve'; streak: number }) {
	const m = useMoment(kind, { solvedBefore: true });
	const testId = 'moment-result';
	if (m.said) return <Said text={m.said} testId={testId} />;
	if (!m.visible) return null;
	if (kind === 'first_solve')
		return (
			<Card
				art={<Nib pose="cheeky" className="nmo-nib" />}
				title="Tomorrow’s is out at midnight"
				body="Want a reminder in the morning? Sparrow only sends it if you haven’t played."
				yes="Remind me"
				onYes={m.yes}
				onNotNow={m.notNow}
				testId={testId}
			/>
		);
	return (
		<Card
			title={`Reminders for your ${streak}-day streak`}
			body={`Sparrow can remind you in the morning, and again at ${m.saverAt} if your streak’s about to end.`}
			yes="Turn on reminders"
			onYes={m.yes}
			onNotNow={m.notNow}
			testId={testId}
		/>
	);
}

/**
 * On Nest, while notifications are off: the nest just joined or started, else the one on
 * screen. No example notification in it (James, 2 Nov).
 */
export function NestJoinMoment({ solvedBefore, nestName }: { solvedBefore: boolean; nestName: string | null }) {
	const m = useMoment('nest_join', { solvedBefore });
	const testId = 'moment-nest';
	const name = m.nest ?? nestName;
	if (m.said) return <Said text={m.said} testId={testId} />;
	if (!m.visible || !name) return null;
	return (
		<Card
			art={<Nib pose="lookup" className="nmo-nib" />}
			title={`Know when ${name} plays`}
			body="A round-up at 19:30, and nudges when it’s your go."
			yes="Turn on"
			onYes={m.yes}
			onNotNow={m.notNow}
			testId={testId}
		/>
	);
}

/**
 * After a nudge: `offer(name)` puts up the toast with Turn on (ui/Toast with an action) and
 * returns true when the moment may show, else false (the caller shows its usual line). It
 * stays three times as long as a plain toast (it has something to press), and while it has
 * focus or the pointer (Radix holds it).
 */
export function useNudgeMoment({ solvedBefore }: { solvedBefore: boolean }) {
	const { state, push } = useNotifyStatus();
	const [toast, setToast] = useState<{ name: string; serial: number } | null>(null);
	const offer = (name: string): boolean => {
		const today = localDateKey(new Date());
		if (!state || !momentVisible(push.moments, 'nudge_sent', { today, state, solvedBefore })) return false;
		noteMomentShown('nudge_sent', today);
		track('notify_ask_shown', { surface: 'nudge_sent' });
		setToast((t) => ({ name, serial: (t?.serial ?? 0) + 1 }));
		return true;
	};
	const node = toast ? (
		<Toast
			key={toast.serial}
			place="above-tabbar"
			title={`Nudge sent to ${toast.name}.`}
			text={`Turn on notifications and ${toast.name} can nudge you back.`}
			duration={tokenMs('--dur-toast', 2400) * 3}
			testId="moment-nudge"
			open
			onOpenChange={(o) => {
				if (!o) setToast(null);
			}}
			action={{
				label: 'Turn on',
				altText: 'Turn on notifications',
				onAction: () => {
					track('notify_ask_tapped', { surface: 'nudge_sent' });
					openNotifySheet({ surface: 'nudge_sent', first: 'nests' });
				},
			}}
		/>
	) : null;
	return { offer, node };
}
